/**
 * LearnerTest Page
 *
 * Main learner test-taking interface. Renders with minimal chrome (no sidebar/nav).
 * Supports three assessment types:
 * - recording: Static audio recording (legacy flow)
 * - conversation: Live voice conversation with AI agent (new orb/onboarding flow)
 * - quiz: Multiple choice quiz
 *
 * Conversation flow uses a multi-screen state machine:
 *   welcome -> mictest -> conversation -> submitting -> complete
 *
 * Recording and quiz paths remain unchanged from v1.
 *
 * Implements all 5 PRD user stories:
 * - US1: Open assessment link
 * - US2: Record audio OR start voice conversation
 * - US3: Submit recording OR end conversation
 * - US4: View confirmation (with optional score)
 * - US5: Retry failed submission
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { apiClient, createApiClientWithToken } from '../../lib/api';
import { sha256Blob } from '../../lib/hash';
import { AudioRecorder } from '../../components/AudioRecorder';
import { QuizSession } from '../../components/QuizSession';
import {
  createTranscriptArtifact,
  type TranscriptArtifactV1,
} from '../../lib/gemini/artifact-schema';
import type { VapTranscriptTurn } from '../../lib/gemini';
import type { PhaseConfig } from '../../lib/gemini/provider';
import type { Assessment, CreateSubmissionResponse, QuizQuestion, SubmitQuizResponse } from '../../lib/types';

// New learner components
import { WelcomeScreen } from '../../components/learner/WelcomeScreen';
import { MicTestScreen } from '../../components/learner/MicTestScreen';
import { ConversationScreen } from '../../components/learner/ConversationScreen';
import { CompletionScreen } from '../../components/learner/CompletionScreen';

// ============================================================================
// State Machine Types
// ============================================================================

/**
 * Page state for recording/quiz flows (legacy).
 * Conversation flow uses ConversationFlowState instead.
 */
type PageState =
  | 'loading'
  | 'ready'
  | 'recorded'
  | 'submitting'
  | 'conversation_submitting'
  | 'complete'
  | 'error';

/**
 * Conversation flow screen state machine.
 * Only used when assessmentType === 'conversation'.
 */
type ConversationFlowState =
  | 'welcome'
  | 'mictest'
  | 'conversation'
  | 'submitting'
  | 'complete'
  | 'error';

/** Assessment type from metadata or assessmentMode */
type AssessmentType = 'recording' | 'conversation' | 'quiz';

interface SubmissionData {
  blob: Blob;
  mimeType: string;
}

/** Score result to display to learner (if allowed) */
interface ScoreResult {
  overallScore: number;
  normalizedScore: number;
  passed: boolean;
}

const MAX_RETRIES = 3;
const LINK_RETRIES = 2;
const MAX_CONVERSATION_RETRIES = 2;

// ============================================================================
// Helpers
// ============================================================================

/**
 * Build a combined system prompt with phase instructions (F-004).
 */
function buildCombinedPhasePrompt(
  warmupPrompt: string,
  inCharacterPrompt: string,
  transitionMessage: string,
  warmupDurationMs: number
): string {
  const warmupSeconds = Math.round(warmupDurationMs / 1000);

  const effectiveWarmupPrompt = warmupPrompt || `
During the warm-up phase (approximately the first ${warmupSeconds} seconds):
- You may briefly break character to help the learner understand the process
- Introduce yourself and explain what will happen during the assessment
- Ask if the learner has any questions before beginning
- Keep this phase brief and supportive`;

  return `## Session Phase Instructions

This voice session has two phases: WARM-UP and ASSESSMENT.

### WARM-UP PHASE (first ~${warmupSeconds} seconds)
${effectiveWarmupPrompt}

### TRANSITION
After the warm-up period, you will receive a signal (or ~${warmupSeconds} seconds will pass).
${transitionMessage}

### ASSESSMENT PHASE
Once the warm-up is complete, fully commit to your character and maintain it throughout.

${inCharacterPrompt}

---
IMPORTANT: Start in WARM-UP mode. Transition to ASSESSMENT mode after the warm-up period.
`.trim();
}

interface PendingLinkData {
  artifactId: string;
  artifactVersionId: string;
  assessmentId: string;
}

/**
 * Tracks partial conversation submission state for idempotent retry.
 */
interface ConversationSubmissionState {
  transcript: VapTranscriptTurn[];
  transcriptArtifact: TranscriptArtifactV1;
  jsonBlob: Blob;
  artifactId: string | null;
  uploadUrl: string | null;
  pendingVersionId: string | null;
  storagePath: string | null;
  artifactVersionId: string | null;
  linkCreated: boolean;
  scoringTriggered: boolean;
}

// ============================================================================
// Component
// ============================================================================

export function LearnerTest() {
  const { assessmentId } = useParams<{ assessmentId: string }>();
  const [searchParams] = useSearchParams();

  const urlToken = searchParams.get('token');

  const client = useMemo(() => {
    if (urlToken) {
      return createApiClientWithToken(urlToken);
    }
    return apiClient;
  }, [urlToken]);

  // ---- Shared state ----
  const [pageState, setPageState] = useState<PageState>('loading');
  const [assessment, setAssessment] = useState<Assessment | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [submissionId, setSubmissionId] = useState<string | null>(null);
  const [linkFailed, setLinkFailed] = useState(false);
  const [pendingLink, setPendingLink] = useState<PendingLinkData | null>(null);

  // ---- Voice conversation state ----
  const [sessionStartTime, setSessionStartTime] = useState<string | null>(null);
  const [scoreResult, setScoreResult] = useState<ScoreResult | null>(null);
  const [scoringFailed, setScoringFailed] = useState(false);
  const [linkRetryError, setLinkRetryError] = useState<string | null>(null);
  const [conversationRetryCount, setConversationRetryCount] = useState(0);

  // ---- Conversation flow state machine ----
  const [conversationFlow, setConversationFlow] = useState<ConversationFlowState>('welcome');

  // ---- Quiz state (F-QUIZ-005) ----
  const [questions, setQuestions] = useState<QuizQuestion[] | null>(null);
  const [questionsError, setQuestionsError] = useState<string | null>(null);
  const [questionsLoading, setQuestionsLoading] = useState(false);

  const conversationSubmissionRef = useRef<ConversationSubmissionState | null>(null);

  // ---- Assessment type ----
  const assessmentType: AssessmentType = useMemo(() => {
    if (assessment?.assessmentMode === 'quiz') return 'quiz';
    // Voice mode defaults to 'conversation' (live AI agent)
    // Falls back to 'recording' only if explicitly set (legacy)
    const type = assessment?.metadata?.assessmentType;
    if (type === 'recording') return 'recording';
    return 'conversation';
  }, [assessment]);

  // Set session start time when entering ready state for conversation assessments
  useEffect(() => {
    if (pageState === 'ready' && assessmentType === 'conversation') {
      setSessionStartTime(new Date().toISOString());
    }
  }, [pageState, assessmentType]);

  // Load questions for quiz mode (F-QUIZ-005)
  useEffect(() => {
    if (pageState !== 'ready' || assessmentType !== 'quiz' || !assessmentId) {
      return;
    }
    if (questions !== null || questionsLoading) {
      return;
    }

    async function loadQuestions() {
      setQuestionsLoading(true);
      setQuestionsError(null);

      try {
        const result = await client.getAssessmentPackage(assessmentId!);

        if (result.response.error || !result.response.data) {
          if (result.response.status === 404) {
            setQuestionsError('Quiz not ready. Please contact your trainer.');
          } else {
            setQuestionsError('Unable to load quiz. Please try again.');
          }
          return;
        }

        const pkg = result.response.data;
        if (!pkg.questions || pkg.questions.length === 0) {
          setQuestionsError('No questions available for this quiz.');
          return;
        }

        setQuestions(pkg.questions);
      } catch (err) {
        console.error('[LearnerTest] Failed to load questions:', err);
        setQuestionsError('Unable to load quiz. Please try again.');
      } finally {
        setQuestionsLoading(false);
      }
    }

    loadQuestions();
  }, [pageState, assessmentType, assessmentId, questions, questionsLoading, client]);

  // Store recording data for retry
  const recordingRef = useRef<SubmissionData | null>(null);
  const pendingSubmissionRef = useRef<CreateSubmissionResponse | null>(null);
  const externalReferenceIdRef = useRef<string | null>(null);

  // Load assessment on mount (US1)
  useEffect(() => {
    async function loadAssessment() {
      if (!assessmentId) {
        setLoadError('Assessment not found. Check your link and try again.');
        setPageState('error');
        return;
      }

      if (!urlToken) {
        setLoadError('Invalid or expired link. Please request a new link from your trainer.');
        setPageState('error');
        return;
      }

      setPageState('loading');
      setLoadError(null);

      const result = await client.getAssessment(assessmentId);

      if (result.response.error || !result.response.data) {
        if (result.response.status === 404) {
          setLoadError('Assessment not found. Check your link and try again.');
        } else if (result.response.status === 0) {
          setLoadError('Unable to load assessment. Check your connection.');
        } else {
          setLoadError('Unable to load assessment. Check your connection.');
        }
        setPageState('error');
        return;
      }

      setAssessment(result.response.data);
      setPageState('ready');
    }

    loadAssessment();
  }, [assessmentId, urlToken, client]);

  // ---- Recording handlers (unchanged) ----

  const handleRecordingComplete = useCallback((blob: Blob, mimeType: string) => {
    const baseMimeType = mimeType.split(';')[0] ?? mimeType;
    recordingRef.current = { blob, mimeType: baseMimeType };
    setPageState('recorded');
    setSubmitError(null);
    setRetryCount(0);
    pendingSubmissionRef.current = null;
    externalReferenceIdRef.current = `learner-${assessmentId}-${Date.now()}`;
  }, [assessmentId]);

  const handleRecordingClear = useCallback(() => {
    recordingRef.current = null;
    pendingSubmissionRef.current = null;
    externalReferenceIdRef.current = null;
    setPageState('ready');
    setSubmitError(null);
    setRetryCount(0);
  }, []);

  const handleSubmit = useCallback(async () => {
    const recording = recordingRef.current;
    if (!recording) return;

    setPageState('submitting');
    setSubmitError(null);

    try {
      let submission = pendingSubmissionRef.current;

      if (!submission) {
        const createResult = await client.createSubmission({
          mimeType: recording.mimeType,
          rubricId: assessment?.rubricVersion || undefined,
          externalReferenceId: externalReferenceIdRef.current || undefined,
        });

        if (createResult.response.error || !createResult.response.data) {
          throw new Error('Unable to start submission. Please try again.');
        }

        submission = createResult.response.data;
        pendingSubmissionRef.current = submission;
      }

      const uploadResult = await client.uploadToPresignedUrl(
        submission.uploadUrl,
        recording.blob,
        recording.mimeType
      );

      if (!uploadResult.success) {
        throw new Error('Upload failed. Please try again.');
      }

      const contentHash = await sha256Blob(recording.blob);
      const contentSize = recording.blob.size;

      const commitResult = await client.commitSubmission(submission.submissionId, {
        artifactId: submission.artifactId,
        contentHash,
        contentSize,
        pendingVersionId: submission.pendingVersionId,
        storagePath: submission.storagePath,
      });

      if (commitResult.response.error || !commitResult.response.data) {
        throw new Error('Submission rejected. Please try again.');
      }

      let linkSucceeded = false;
      const linkData: PendingLinkData | null = assessmentId ? {
        artifactId: submission.artifactId,
        artifactVersionId: commitResult.response.data.artifactVersionId,
        assessmentId: assessmentId,
      } : null;

      if (linkData) {
        for (let attempt = 0; attempt <= LINK_RETRIES; attempt++) {
          const linkResult = await client.createArtifactLink({
            ...linkData,
            linkType: 'source',
          });

          if (!linkResult.response.error || linkResult.response.status === 409) {
            linkSucceeded = true;
            break;
          }

          if (attempt === LINK_RETRIES) break;
          await new Promise((resolve) => setTimeout(resolve, 500));
        }
      } else {
        linkSucceeded = true;
      }

      setSubmissionId(commitResult.response.data.submissionId);
      setPageState('complete');

      if (!linkSucceeded && linkData) {
        setLinkFailed(true);
        setPendingLink(linkData);
      } else {
        setLinkFailed(false);
        setPendingLink(null);
      }

      recordingRef.current = null;
      pendingSubmissionRef.current = null;
      externalReferenceIdRef.current = null;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Submission failed. Please try again.';
      setSubmitError(message);
      setRetryCount((prev) => prev + 1);
      setPageState('recorded');
    }
  }, [assessment?.rubricVersion, assessmentId, client]);

  const handleRetry = useCallback(() => {
    if (retryCount >= MAX_RETRIES) {
      setSubmitError('Submission failed repeatedly. Please contact support.');
      return;
    }
    handleSubmit();
  }, [retryCount, handleSubmit]);

  // ---- Conversation helpers (shared) ----

  const agentPrompt: string = useMemo(() => {
    const prompt = assessment?.metadata?.agentPrompt;
    return typeof prompt === 'string' ? prompt : '';
  }, [assessment]);

  const showScoreToLearner: boolean = useMemo(() => {
    return assessment?.metadata?.showScoreToLearner === true;
  }, [assessment]);

  const phaseConfig: PhaseConfig | undefined = useMemo(() => {
    const metadata = assessment?.metadata;
    if (!metadata || assessmentType !== 'conversation') {
      return undefined;
    }

    const phaseControlEnabled = metadata.phaseControlEnabled === true;
    const warmupDurationMs = typeof metadata.warmupDurationMs === 'number'
      ? metadata.warmupDurationMs
      : 30000;

    if (!phaseControlEnabled && metadata.warmupDurationMs === undefined) {
      return undefined;
    }

    const warmupPrompt = typeof metadata.warmupPrompt === 'string'
      ? metadata.warmupPrompt
      : '';
    const inCharacterPrompt = typeof metadata.inCharacterPrompt === 'string'
      ? metadata.inCharacterPrompt
      : (agentPrompt || '');
    const transitionMessage = typeof metadata.transitionMessage === 'string'
      ? metadata.transitionMessage
      : 'The warm-up phase is now complete. Please continue with the assessment.';

    const systemPrompt = buildCombinedPhasePrompt(
      warmupPrompt,
      inCharacterPrompt,
      transitionMessage,
      warmupDurationMs
    );

    return {
      warmupDurationMs,
      systemPrompt,
    };
  }, [assessment, assessmentType, agentPrompt]);

  const roleName: string | undefined = useMemo(() => {
    const name = assessment?.metadata?.personaRole ?? assessment?.metadata?.roleName;
    return typeof name === 'string' ? name : undefined;
  }, [assessment]);

  // ---- Conversation submission (shared between old and new flow) ----

  const handleConversationEnd = useCallback(async (transcript: VapTranscriptTurn[]) => {
    if (!assessmentId) return;

    if (transcript.length === 0) {
      setSubmitError('No conversation recorded. Please try again.');
      setConversationFlow('error');
      setPageState('error');
      return;
    }

    setConversationFlow('submitting');
    setPageState('conversation_submitting');
    setSubmitError(null);
    setScoringFailed(false);

    try {
      let state = conversationSubmissionRef.current;

      if (!state) {
        const sessionEndedAt = new Date().toISOString();
        const sessionStartedAt = transcript[0]?.timestamp || sessionStartTime || sessionEndedAt;

        const transcriptArtifact: TranscriptArtifactV1 = createTranscriptArtifact(
          transcript.map((t) => ({
            role: t.role,
            text: t.text,
            timestamp: t.timestamp,
            phase: t.phase,
          })),
          'gemini',
          sessionStartedAt,
          sessionEndedAt
        );

        const jsonContent = JSON.stringify(transcriptArtifact, null, 2);
        const jsonBlob = new Blob([jsonContent], { type: 'application/json' });

        state = {
          transcript,
          transcriptArtifact,
          jsonBlob,
          artifactId: null,
          uploadUrl: null,
          pendingVersionId: null,
          storagePath: null,
          artifactVersionId: null,
          linkCreated: false,
          scoringTriggered: false,
        };
        conversationSubmissionRef.current = state;
      }

      // Step 1: Init artifact
      if (!state.artifactId) {
        const initResult = await client.initArtifact({
          mimeType: 'application/json',
          filename: `transcript-${assessmentId}.json`,
          metadata: {
            schemaVersion: state.transcriptArtifact.schemaVersion,
            provider: state.transcriptArtifact.provider,
            assessmentId,
          },
        });

        if (initResult.response.error || !initResult.response.data) {
          throw new Error('Unable to initialize transcript artifact. Please try again.');
        }

        const artifact = initResult.response.data;
        state.artifactId = artifact.id;
        state.uploadUrl = artifact.uploadUrl;
        state.pendingVersionId = artifact.pendingVersionId;
        state.storagePath = artifact.storagePath;
      }

      // Step 2: Upload
      if (!state.artifactVersionId && state.uploadUrl) {
        const uploadResult = await client.uploadToPresignedUrl(
          state.uploadUrl,
          state.jsonBlob,
          'application/json'
        );

        if (!uploadResult.success) {
          throw new Error('Upload failed. Please try again.');
        }
      }

      // Step 3: Commit
      if (!state.artifactVersionId && state.artifactId && state.pendingVersionId && state.storagePath) {
        const contentHash = await sha256Blob(state.jsonBlob);
        const contentSize = state.jsonBlob.size;

        const commitResult = await client.commitArtifact(state.artifactId, {
          contentHash,
          contentSize,
          versionId: state.pendingVersionId,
          storagePath: state.storagePath,
        });

        if (commitResult.response.error || !commitResult.response.data) {
          throw new Error('Submission rejected. Please try again.');
        }

        const artifactVersionId = commitResult.response.data.versions[0]?.id;
        if (!artifactVersionId) {
          console.error('[LearnerTest] Commit succeeded but no version ID:', {
            assessmentId,
            artifactId: state.artifactId,
            commitResponse: commitResult.response.data,
          });
          throw new Error('Artifact version not found after commit.');
        }
        state.artifactVersionId = artifactVersionId;
      }

      // Step 4: Link artifact
      const linkData: PendingLinkData | null = state.artifactId && state.artifactVersionId ? {
        artifactId: state.artifactId,
        artifactVersionId: state.artifactVersionId,
        assessmentId,
      } : null;

      if (!state.linkCreated && linkData) {
        for (let attempt = 0; attempt <= LINK_RETRIES; attempt++) {
          const linkResult = await client.createArtifactLink({
            ...linkData,
            linkType: 'source',
          });

          if (!linkResult.response.error || linkResult.response.status === 409) {
            state.linkCreated = true;
            break;
          }

          if (attempt === LINK_RETRIES) break;
          await new Promise((resolve) => setTimeout(resolve, 500));
        }
      }

      // Step 5: Trigger scoring
      if (!state.scoringTriggered && assessment?.rubricVersion && state.artifactVersionId) {
        const scoringResponse = await client.generateScore({
          transcriptArtifactVersionId: state.artifactVersionId,
          rubricId: assessment.rubricVersion,
        });

        state.scoringTriggered = true;

        if (scoringResponse.response.error) {
          console.error('[LearnerTest] Scoring failed:', {
            assessmentId,
            artifactVersionId: state.artifactVersionId,
            rubricId: assessment.rubricVersion,
            status: scoringResponse.response.status,
            error: scoringResponse.response.error,
          });
          setScoringFailed(true);
        } else if (scoringResponse.response.data) {
          if (showScoreToLearner) {
            setScoreResult({
              overallScore: scoringResponse.response.data.overallScore,
              normalizedScore: scoringResponse.response.data.normalizedScore,
              passed: scoringResponse.response.data.passed,
            });
          }
        }
      }

      // Success
      setSubmissionId(state.artifactId);
      setConversationFlow('complete');
      setPageState('complete');
      conversationSubmissionRef.current = null;

      if (!state.linkCreated && linkData) {
        setLinkFailed(true);
        setPendingLink(linkData);
      } else {
        setLinkFailed(false);
        setPendingLink(null);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Submission failed. Please try again.';
      console.error('[LearnerTest] Conversation submission failed:', err);
      setSubmitError(message);
      setConversationRetryCount((prev) => prev + 1);
      setConversationFlow('error');
      setPageState('error');
    }
  }, [assessmentId, assessment?.rubricVersion, client, sessionStartTime, showScoreToLearner]);

  const handleConversationError = useCallback((error: Error) => {
    console.error('[LearnerTest] Conversation error:', error);
    setSubmitError(error.message);
  }, []);

  const handleConversationRetry = useCallback(() => {
    if (conversationRetryCount >= MAX_CONVERSATION_RETRIES) {
      setSubmitError('Submission failed repeatedly. Please contact support.');
      return;
    }
    const state = conversationSubmissionRef.current;
    if (state) {
      handleConversationEnd(state.transcript);
    }
  }, [conversationRetryCount, handleConversationEnd]);

  // ---- Quiz handlers (unchanged) ----

  const handleQuizComplete = useCallback((result: SubmitQuizResponse) => {
    setSubmissionId('quiz-' + Date.now());
    setPageState('complete');

    if (result.score100 !== undefined && result.passed !== undefined) {
      setScoreResult({
        overallScore: result.score100 / 100,
        normalizedScore: result.score100 / 100,
        passed: result.passed,
      });
    }
  }, []);

  const handleQuizError = useCallback((error: Error) => {
    console.error('[LearnerTest] Quiz error:', error);
    setSubmitError(error.message);
  }, []);

  const handleRetryLoadQuestions = useCallback(() => {
    setQuestions(null);
    setQuestionsError(null);
    setQuestionsLoading(false);
  }, []);

  const handleRetryLink = useCallback(async () => {
    if (!pendingLink) return;

    setLinkRetryError(null);

    const linkResult = await client.createArtifactLink({
      ...pendingLink,
      linkType: 'source',
    });

    if (!linkResult.response.error || linkResult.response.status === 409) {
      setLinkFailed(false);
      setPendingLink(null);
    } else {
      console.error('[LearnerTest] Retry link failed:', linkResult.response.error);
      setLinkRetryError('Link retry failed. Please try again or contact support.');
    }
  }, [pendingLink, client]);

  // ============================================================================
  // Render: Loading
  // ============================================================================

  if (pageState === 'loading') {
    return (
      <div className="learner-container">
        <div className="learner-content">
          <div className="learner-card">
            <p className="text-muted">Loading assessment...</p>
          </div>
        </div>
      </div>
    );
  }

  // ============================================================================
  // Render: Error (assessment load error)
  // ============================================================================

  if (pageState === 'error' && loadError) {
    return (
      <div className="learner-container">
        <div className="learner-content">
          <div className="error-box">{loadError}</div>
        </div>
      </div>
    );
  }

  // ============================================================================
  // Render: Conversation flow (new multi-screen state machine)
  // ============================================================================

  if (assessmentType === 'conversation') {
    // Conversation flow complete -> show CompletionScreen
    if (conversationFlow === 'complete' || pageState === 'complete') {
      return (
        <div className="learner-container">
          <div className="learner-content">
            <CompletionScreen
              submissionId={submissionId}
              showScoreToLearner={showScoreToLearner}
              scoreResult={scoreResult}
              scoringFailed={scoringFailed}
              roleName={roleName}
            />

            {/* Link failure warning with retry */}
            {linkFailed && pendingLink && (
              <div className="error-box mt-4">
                <p className="mb-4">
                  Your conversation was submitted, but linking to the assessment failed.
                  This may affect how your submission appears to the evaluator.
                </p>
                {linkRetryError && (
                  <p className="text-small text-error mb-4">{linkRetryError}</p>
                )}
                <button
                  className="btn btn-secondary"
                  onClick={handleRetryLink}
                >
                  Retry Link
                </button>
              </div>
            )}
          </div>
        </div>
      );
    }

    // Conversation submitting
    if (conversationFlow === 'submitting') {
      return (
        <div className="learner-container">
          <div className="learner-content" style={{ textAlign: 'center', paddingTop: '20vh' }}>
            <div className="learner-card">
              <p className="text-muted">Processing your conversation transcript...</p>
            </div>
          </div>
        </div>
      );
    }

    // Conversation error (submission error, not load error)
    if (conversationFlow === 'error' && submitError && !loadError) {
      return (
        <div className="learner-container">
          <div className="learner-content">
            <div className="error-box">
              <p className="mb-4">{submitError}</p>
              {conversationRetryCount >= MAX_CONVERSATION_RETRIES ? (
                <p className="text-small">
                  Submission failed repeatedly. Please contact support.
                </p>
              ) : conversationSubmissionRef.current ? (
                <button
                  className="btn btn-primary"
                  onClick={handleConversationRetry}
                >
                  Retry Submission ({MAX_CONVERSATION_RETRIES - conversationRetryCount} attempts remaining)
                </button>
              ) : null}
            </div>
          </div>
        </div>
      );
    }

    // Welcome screen
    if (conversationFlow === 'welcome' && assessment) {
      return (
        <div className="learner-container">
          <div className="learner-content">
            <WelcomeScreen
              assessment={assessment}
              onBegin={() => setConversationFlow('mictest')}
            />
          </div>
        </div>
      );
    }

    // Mic test screen
    if (conversationFlow === 'mictest') {
      return (
        <div className="learner-container">
          <div className="learner-content">
            <MicTestScreen
              onStartCall={() => setConversationFlow('conversation')}
              onBack={() => setConversationFlow('welcome')}
            />
          </div>
        </div>
      );
    }

    // Active conversation (full-screen orb)
    if (conversationFlow === 'conversation') {
      return (
        <div className="learner-container learner-conversation-fullscreen">
          <ConversationScreen
            agentPrompt={agentPrompt}
            assessmentId={assessmentId || ''}
            authToken={urlToken || undefined}
            phaseConfig={phaseConfig}
            onSessionEnd={handleConversationEnd}
            onError={handleConversationError}
          />
        </div>
      );
    }
  }

  // ============================================================================
  // Render: Completion (recording/quiz - legacy)
  // ============================================================================

  if (pageState === 'complete') {
    return (
      <div className="learner-container">
        <div className="learner-content">
          <div className="success-box">
            <h2 className="mb-4">Submission Complete</h2>
            <p className="mb-4">
              {assessmentType === 'quiz'
                ? 'Your quiz has been submitted and scored.'
                : 'Your recording has been received and queued for evaluation.'}
            </p>

            {showScoreToLearner && scoreResult && (
              <div className="learner-score-box mt-4 mb-4">
                <div className={`learner-score ${scoreResult.passed ? 'passed' : 'not-passed'}`}>
                  <span className="score-label">Your Score:</span>
                  <span className="score-value">
                    {Math.round(scoreResult.normalizedScore * 100)}%
                  </span>
                </div>
                <div className={`score-status ${scoreResult.passed ? 'passed' : 'not-passed'}`}>
                  {scoreResult.passed ? 'Passed' : 'Did not pass'}
                </div>
              </div>
            )}

            {showScoreToLearner && scoringFailed && !scoreResult && (
              <div className="warning-box mt-4 mb-4">
                <p>Score calculation is in progress. Check back later or contact your trainer.</p>
              </div>
            )}

            <p className="text-small text-muted">
              Reference ID:{' '}
              <span className="mono">{submissionId}</span>
            </p>
            <p className="mt-4 text-small">
              You may close this page.
            </p>
          </div>

          {linkFailed && pendingLink && (
            <div className="error-box mt-4">
              <p className="mb-4">
                Your {assessmentType === 'quiz' ? 'quiz' : 'recording'} was submitted, but linking to the assessment failed.
                This may affect how your submission appears to the evaluator.
              </p>
              {linkRetryError && (
                <p className="text-small text-error mb-4">{linkRetryError}</p>
              )}
              <button
                className="btn btn-secondary"
                onClick={handleRetryLink}
              >
                Retry Link
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ============================================================================
  // Render: Main test interface (recording/quiz paths)
  // ============================================================================

  return (
    <div className="learner-container">
      <div className="learner-content">
        {/* Header */}
        <div className="learner-header">
          <h1>{assessmentType === 'quiz' ? 'Quiz Assessment' : 'Voice Assessment'}</h1>
          <p className="text-muted">
            {assessmentType === 'quiz'
              ? 'Answer the questions below to complete your assessment.'
              : 'Record your response and submit when ready.'}
          </p>
        </div>

        {/* Assessment context */}
        {assessment && (
          <div className="learner-card">
            <div className="text-small text-muted mb-4">
              Assessment
            </div>
            <div className="mono text-small">
              {assessment.externalReferenceId || assessment.id}
            </div>
          </div>
        )}

        {/* Quiz Session for quiz assessments (F-QUIZ-005) */}
        {assessmentType === 'quiz' && (
          <>
            {questionsLoading && (
              <div className="learner-card">
                <div className="quiz-loading">
                  <div className="spinner" />
                  <p>Loading quiz questions...</p>
                </div>
              </div>
            )}

            {questionsError && (
              <div className="error-box">
                <p className="mb-4">{questionsError}</p>
                <button
                  className="btn btn-secondary"
                  onClick={handleRetryLoadQuestions}
                >
                  Retry
                </button>
              </div>
            )}

            {questions && questions.length > 0 && !questionsError && (
              <QuizSession
                questions={questions}
                assessmentId={assessmentId!}
                client={client}
                onComplete={handleQuizComplete}
                onError={handleQuizError}
                disabled={pageState === 'submitting'}
              />
            )}
          </>
        )}

        {/* Audio recorder for recording assessments (US2) */}
        {assessmentType === 'recording' && (
          <>
            <AudioRecorder
              onRecordingComplete={handleRecordingComplete}
              onRecordingClear={handleRecordingClear}
              disabled={pageState === 'submitting'}
            />

            {submitError && (
              <div className="error-box mt-4">
                {submitError}
              </div>
            )}

            {(pageState === 'recorded' || submitError) && (
              <div className="mt-4">
                {retryCount >= MAX_RETRIES ? (
                  <p className="text-small text-error">
                    Submission failed repeatedly. Please contact support.
                  </p>
                ) : (
                  <button
                    className="btn btn-primary learner-submit-btn"
                    onClick={submitError ? handleRetry : handleSubmit}
                    disabled={pageState === 'submitting'}
                  >
                    {pageState === 'submitting'
                      ? 'Submitting...'
                      : submitError
                        ? `Retry (${MAX_RETRIES - retryCount} attempts remaining)`
                        : 'Submit Recording'}
                  </button>
                )}
              </div>
            )}

            {pageState === 'submitting' && (
              <div className="mt-4 text-muted text-center">
                Uploading and processing your recording...
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
